import React from "react";

/**
 * App-wide React error boundary. Prevents an unhandled render/effect
 * exception in one route from taking down the entire dashboard. Rendered
 * around <Routes /> in App.jsx.
 *
 * The fallback UI intentionally stays inside brand voice (no stack
 * traces in production) but still surfaces enough context for the user
 * to recover — reload, sign out, or navigate home.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep console breadcrumbs for local debugging; in production this
    // could ship to Sentry / a backend endpoint if desired later.
    console.error("[ErrorBoundary] Unhandled render error:", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleHome = () => {
    try { window.location.assign("/dashboard"); } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "";
    return (
      <div
        className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-lg shadow-sm p-8">
          <div className="font-overline text-slate-500 mb-3">Something went wrong</div>
          <h1 className="ntn-h2 mb-3">We hit a snag rendering this page.</h1>
          <p className="ntn-body text-slate-600 mb-6">
            The rest of NTN Verify Hub is still up. Try reloading, or head
            back to your dashboard to keep moving.
          </p>
          {process.env.NODE_ENV !== "production" && msg ? (
            <pre
              className="text-xs bg-slate-50 text-slate-700 border border-slate-200 rounded p-3 mb-5 overflow-auto max-h-40"
              data-testid="error-boundary-message"
            >{msg}</pre>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload-btn"
              className="inline-flex items-center rounded-md bg-[#0A192F] hover:bg-[#0A192F]/90 text-white text-sm font-medium px-4 py-2"
            >
              Reload page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-boundary-home-btn"
              className="inline-flex items-center rounded-md border border-slate-300 hover:bg-slate-100 text-slate-700 text-sm font-medium px-4 py-2"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
